import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { optionalSupabaseAuth } from "@/integrations/supabase/auth-middleware-optional";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const logInput = z.object({
  partner: z.string().max(32).default("avvo"),
  source: z.string().min(1).max(64),
  riskScore: z.number().int().min(0).max(100).nullable().optional(),
  riskLevel: z.enum(["low", "medium", "high"]).nullable().optional(),
  toolSlug: z.string().max(64).nullable().optional(),
  referrerUrl: z.string().max(2048).nullable().optional(),
  userAgent: z.string().max(512).nullable().optional(),
});

function genClickId(): string {
  // 22-char URL-safe id (96 bits) — small but collision-safe for click volume.
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export const logReferralClick = createServerFn({ method: "POST" })
  .middleware([optionalSupabaseAuth])
  .inputValidator((input: unknown) => logInput.parse(input))
  .handler(async ({ data, context }) => {
    const click_id = genClickId();
    const { error } = await supabaseAdmin.from("referral_clicks").insert({
      click_id,
      partner: data.partner,
      source: data.source,
      user_id: context.userId ?? null,
      risk_score: data.riskScore ?? null,
      risk_level: data.riskLevel ?? null,
      tool_slug: data.toolSlug ?? null,
      referrer_url: data.referrerUrl ?? null,
      user_agent: data.userAgent ?? null,
    });
    if (error) {
      console.error("referral_clicks insert failed", error);
      // Never block the user's outbound click on logging failure.
      return { ok: false as const, click_id };
    }
    return { ok: true as const, click_id };
  });
